'use client';

import { useEffect, useState } from 'react';
import { PortableText } from '@portabletext/react';

import { GroqImage } from '@/helpers/Image';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/shadcn/ui/carousel';
import type { PageRizAffiliateSchema } from '@/sanity/pages/PageRizAffiliateTemplate';
import { measuredRichText, V3_HIGHLIGHT } from './richtext';
import ChevronCta from './ChevronCta';
import { RevealBlock, RevealEyebrow, RevealRichText, RevealStyles } from './about/reveal';
import { CarouselArrow } from './final/CarouselArrow';

import noise from '@/public/fabio-affiliate/noise.jpg';

const HEADING_CLASS = `${V3_HIGHLIGHT} richtext richtext-center w-[min(28.75rem,100%)] lg:w-auto prose-headings:text-black prose-headings:font-geist prose-headings:font-[500] prose-headings:text-[4.5rem] prose-headings:tracking-[-0.225rem] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.3rem] [--hl-tracking:-0.03em] [--hl-wash:4.5rem] lg:[--hl-wash:6.75rem] [--hl-dy:0.125rem] lg:[--hl-dy:0rem] lg:[--hl-pl:0.925rem] lg:[--hl-ml:1.5rem] lg:[--hl-mr:1.2rem] lg:[--hl-tl:0.875rem] prose-headings:leading-none lg:prose-headings:leading-[1.15]`;

const QUOTE_CLASS =
  'richtext richtext-start capitalize w-full [text-wrap:wrap] prose-p:font-geist prose-p:font-[500] prose-p:text-[#103037] prose-p:leading-[1.444] prose-p:text-[2.25rem] prose-p:tracking-[-0.075rem] lg:prose-p:leading-[1.4] lg:prose-p:text-[3rem] lg:prose-p:tracking-[-0.09rem] prose-headings:font-geist prose-headings:font-[500] prose-headings:text-[#103037] prose-headings:leading-[1.444] prose-headings:text-[2.25rem] prose-headings:tracking-[-0.075rem] lg:prose-headings:leading-[1.4] lg:prose-headings:text-[3rem] lg:prose-headings:tracking-[-0.09rem] lg:[&>*]:[text-box-edge:cap_alphabetic] lg:[&>*]:[text-box-trim:trim-both]';

export default function TradersSaySection({
  data,
}: {
  data: PageRizAffiliateSchema;
}) {
  const items = data?.tradersSayItems ?? [];
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    const sync = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };
    sync();
    api.on('select', sync);
    api.on('reInit', sync);
    return () => {
      api.off('select', sync);
      api.off('reInit', sync);
    };
  }, [api]);

  if (
    !data?.tradersSayChip &&
    !data?.tradersSayHeading?.length &&
    !items.length &&
    !data?.tradersSayCTA
  )
    return null;

  const progress = count > 0 ? ((current + 1) / count) * 100 : 0;

  return (
    <section className="relative overflow-hidden bg-[#B4B4B4]/[15%] pt-[8rem] pb-[8.125rem] lg:pt-[13.875rem] lg:pb-[13.75rem]">
      <RevealStyles />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.09] mix-blend-exclusion pointer-events-none bg-no-repeat bg-[length:296rem_152.25rem] bg-[position:0_-0.625rem]"
        style={{ backgroundImage: `url(${noise.src})` }}
      />

      <div className="flex relative flex-col items-center w-full gap-[4.5rem] lg:gap-[6.75rem]">
        {(data?.tradersSayChip || data?.tradersSayHeading?.length > 0) && (
          <div className="flex flex-col items-center px-[2rem] gap-[2rem] lg:gap-[1.5rem]">
            <RevealEyebrow text={data?.tradersSayChip} />
            <RevealRichText
              value={data?.tradersSayHeading}
              components={measuredRichText}
              className={HEADING_CLASS}
            />
          </div>
        )}

        <div className="flex flex-col items-center w-full gap-[3rem] lg:gap-[7rem]">
          {items.length > 0 && (
            <div className="flex flex-col items-center w-full gap-[3.125rem] lg:gap-[5rem]">
              <RevealBlock className="w-full">
                <Carousel
                  setApi={setApi}
                  opts={{ loop: true, align: 'center' }}
                  className="w-full"
                >
                  <CarouselContent>
                    {items.map((item, i) => (
                      <CarouselItem
                        key={`${item?.name ?? 'trader'}-${i}`}
                        className="basis-auto px-[0.5rem] lg:px-[1.625rem]"
                      >
                        <article className="flex flex-col items-start bg-white rounded-[0.75rem] overflow-clip w-[min(49.75rem,calc(100vw-4rem))] p-[2rem] gap-[1.875rem] lg:flex-row lg:items-center lg:w-auto lg:gap-[6.5rem] lg:pl-[4rem] lg:pr-[2rem] lg:py-[2rem]">
                          <div className="flex flex-col justify-between w-full min-h-[29.625rem] p-[1.5rem] lg:w-[min(50.625rem,100vw)] lg:min-h-[45rem] lg:px-0 lg:py-[1.5rem]">
                            <div className="flex flex-col items-start w-full gap-[3.25rem]">
                              {item?.chip && (
                                <span className="bg-[#6DA991]/[9%] font-blacker font-light text-black text-[2rem] leading-[1.5] px-[1.25rem] py-[1rem]">
                                  {item.chip}
                                </span>
                              )}
                              {item?.text?.length > 0 && (
                                <div className={QUOTE_CLASS}>
                                  <PortableText
                                    value={item.text}
                                    components={measuredRichText}
                                  />
                                </div>
                              )}
                            </div>
                            {item?.name && (
                              <p className="m-0 w-full font-geist font-[600] text-[#103037] opacity-50 text-[2rem] leading-[1.11] tracking-[-0.06rem] lg:text-[2.5rem] lg:tracking-[-0.075rem]">
                                {item.name}
                              </p>
                            )}
                          </div>

                          {item?.certificate && (
                            <div
                              className="overflow-hidden relative shrink-0 bg-[#F4F4F4] border border-black/[4%] rounded-[0.5rem] w-full lg:h-[47.25rem] lg:w-auto lg:rounded-[0.875rem]"
                              style={{ aspectRatio: `${item.certificate.width} / ${item.certificate.height}` }}>
                              <GroqImage
                                src={item.certificate}
                                alt={item?.name ?? ''}
                                quality={100}
                                sizes="(min-width: 993px) 32vw, 86vw"
                                style={{ width: '100%' }}
                                className="object-cover !w-full !h-full"
                              />
                            </div>
                          )}
                        </article>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              </RevealBlock>

              <div className="flex items-center justify-center gap-[2.5rem] w-[min(49.75rem,calc(100%-4rem))] lg:w-auto">
                <CarouselArrow
                  direction="prev"
                  onClick={() => api?.scrollPrev()}
                  className="bg-[#C2DED6] text-[#161616]"
                />
                <div className="flex-1 overflow-hidden h-[0.25rem] bg-[#6DA991]/20 lg:flex-none lg:w-[29.25rem]">
                  <div
                    className="h-full bg-[#228E71] transition-[width] duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <CarouselArrow
                  direction="next"
                  onClick={() => api?.scrollNext()}
                  className="bg-[#003828] text-white"
                />
              </div>
            </div>
          )}

          {data?.tradersSayCTA && (
            <RevealBlock className="flex justify-center w-[min(49.75rem,calc(100%-4rem))] lg:w-auto">
              <ChevronCta
                wide
                data={data.tradersSayCTA}
                language={data?.settings?.language}
              />
            </RevealBlock>
          )}
        </div>
      </div>
    </section>
  );
}
